import {
  AddUpdateTaskParams,
  Id,
  RecurringTaskParams,
  Task,
  toDateString,
} from '@nextforce/api'
import { every, includes, isEmpty, isEqualWith } from 'lodash'
import moment from 'moment'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useRecentStoresContext } from 'stores/context'
import { getDefaultEndDateForFrequency, validateRecurringTask } from './utils'

const defaultDueDate = toDateString(moment())

export const DEFAULT_RECURRING_TASK_FIELDS: RecurringTaskParams = {
  byDay: [],
  frequency: 'DAILY',
  interval: 1,
  monthlyOn: 1,
  until: getDefaultEndDateForFrequency('DAILY', defaultDueDate, 1),
}

const DEFAULT_TASK_FIELDS: AddUpdateTaskParams = {
  summary: undefined,
  description: undefined,
  dueDate: defaultDueDate,
  assignedTo: undefined,
  storeId: undefined,
  photoRequired: false,
  recurring: false,
  ...DEFAULT_RECURRING_TASK_FIELDS,
}

export type AddEditTaskContextValue = {
  taskId: Id | undefined
  setTaskId: React.Dispatch<React.SetStateAction<Id | undefined>>
  taskFields: AddUpdateTaskParams
  setTaskFields: React.Dispatch<React.SetStateAction<AddUpdateTaskParams>>
  setTaskFieldsByTaskDetails: (taskDetails: Task | undefined) => void
  isIdenticalEditTaskFields: (existingTask: Task | undefined) => boolean
  isValid: boolean
  resetTaskFields: () => void
}

export const AddEditTaskContext = createContext({} as AddEditTaskContextValue)

interface Props {
  children: React.ReactNode
}

export function AddEditTaskProvider(props: Props) {
  const { activeStoreId } = useRecentStoresContext()
  const [taskId, setTaskId] = useState<Id | undefined>(undefined)

  const defaultTaskFields: AddUpdateTaskParams = useMemo(() => {
    const defaultFields: AddUpdateTaskParams = {
      ...DEFAULT_TASK_FIELDS,
      storeId: activeStoreId,
    }
    return defaultFields
  }, [activeStoreId])

  const [taskFields, setTaskFields] =
    useState<AddUpdateTaskParams>(defaultTaskFields)

  const resetTaskFields = useCallback(() => {
    setTaskFields(defaultTaskFields)
  }, [defaultTaskFields])

  const setTaskFieldsByTaskDetails = useCallback(
    (taskDetails: Task | undefined) => {
      if (!taskDetails || taskDetails.occurrences.length === 0) {
        return
      }

      const occurrence = taskDetails.occurrences[0]

      setTaskFields({
        ...defaultTaskFields,
        summary: occurrence.summary,
        description: occurrence.description,
        dueDate: occurrence.dueDate,
        assignedTo: taskDetails.assignedTo.id,
        storeId: taskDetails.storeId,
        byDay: taskDetails.byDay,
        frequency: taskDetails.frequency,
        interval: taskDetails.interval,
        monthlyOn: taskDetails.monthlyOn,
        until: taskDetails.until,
        photoRequired: occurrence.photoRequired,
        recurring: taskDetails.recurring,
      })
    },
    [defaultTaskFields]
  )

  useEffect(() => {
    setTaskFields(defaultTaskFields)
  }, [defaultTaskFields])

  const isIdenticalEditTaskFields = useCallback(
    (existingTask: Task | undefined) => {
      if (!existingTask) return false
      const existingTaskFields: AddUpdateTaskParams = {
        ...defaultTaskFields,
        summary: existingTask.occurrences[0].summary,
        description: existingTask.occurrences[0].description,
        dueDate: existingTask.occurrences[0].dueDate,
        assignedTo: existingTask.assignedTo.id,
        storeId: existingTask.storeId,
        byDay: existingTask.byDay,
        frequency: existingTask.frequency,
        interval: existingTask.interval,
        monthlyOn: existingTask.monthlyOn,
        until: existingTask.until,
        photoRequired: existingTask.occurrences[0].photoRequired,
        recurring: existingTask.recurring,
      }

      return isEqualWith(
        taskFields,
        existingTaskFields,
        (taskValue, existingValue, key) => {
          if (key === 'byDay') {
            if (!taskValue && !existingValue) return true
            if (!taskValue || !existingValue) return false
            return (
              taskValue.length === existingValue.length &&
              every(taskValue, (day: string) => includes(existingValue, day))
            )
          }

          // Let isEqual handle default comparison for other properties
          return undefined
        }
      )
    },
    [defaultTaskFields, taskFields]
  )

  const isValid = useMemo(() => {
    const valid =
      !isEmpty(taskFields.summary) &&
      !isEmpty(taskFields.dueDate) &&
      !isEmpty(taskFields.assignedTo)

    if (taskFields.recurring) {
      const validRecurring = validateRecurringTask(taskFields)
      return valid && validRecurring
    }

    return valid
  }, [taskFields])

  return (
    <AddEditTaskContext.Provider
      value={{
        taskId,
        setTaskId,
        taskFields,
        setTaskFields,
        setTaskFieldsByTaskDetails,
        isIdenticalEditTaskFields,
        isValid,
        resetTaskFields,
      }}
    >
      {props.children}
    </AddEditTaskContext.Provider>
  )
}

export function useAddEditTaskContext() {
  return useContext<AddEditTaskContextValue>(AddEditTaskContext)
}
